// src/contexts/NotificationSocketContext.js
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useBaseSocket } from './BaseSocketContext';

const NotificationSocketContext = createContext();

export const useNotificationSocket = () => {
    return useContext(NotificationSocketContext);
};

export const NotificationSocketProvider = ({ children }) => {
    const { user } = useAuth();
    const { socket, connected } = useBaseSocket();
    const [activities, setActivities] = useState([]);
    const [notificationCount, setNotificationCount] = useState(0);

    // Fetch initial notifications on component mount
    useEffect(() => {
        if (user) {
            fetchInitialNotifications();
        }
    }, [user]);

    // Memoized notification handlers
    const notificationHandlers = useMemo(() => ({
        handleNewActivity: (activity) => {
            console.log('New activity received:', activity);

            // Add to activities list
            setActivities(prev => {
                // Check if activity already exists by _id to prevent duplicates
                const exists = prev.some(a => a._id === activity._id);
                if (exists) return prev;
                return [activity, ...prev].slice(0, 50); // Keep only 50 most recent
            });

            // Increment notification count if relevant
            if (shouldNotifyUser(activity, user)) {
                setNotificationCount(prev => prev + 1);
                // Show browser notification if permission granted
                showBrowserNotification(activity);
            }
        },

        handleNewNotification: (notification) => {
            console.log('New notification received:', notification);

            // If the notification includes an activityId, find and update that activity
            if (notification.activityId) {
                // Fetch the latest activities to ensure we have the most up-to-date data
                fetchInitialNotifications();
            } else {
                // Increment notification count
                setNotificationCount(prev => prev + 1);
            }
        },

        handleAllActivitiesRead: () => {
            console.log('All activities marked as read');
            markAllNotificationsAsRead();
        }
    }), [user]);

    // Set up notification-related socket listeners
    useEffect(() => {
        if (!socket || !connected) return;

        // Listen for activity notifications
        socket.on('new_activity', notificationHandlers.handleNewActivity);

        // Listen for notification events
        socket.on('new_notification', notificationHandlers.handleNewNotification);

        // Listen for all_activities_read event
        socket.on('all_activities_read', notificationHandlers.handleAllActivitiesRead);

        // Clean up listeners
        return () => {
            socket.off('new_activity', notificationHandlers.handleNewActivity);
            socket.off('new_notification', notificationHandlers.handleNewNotification);
            socket.off('all_activities_read', notificationHandlers.handleAllActivitiesRead);
        };
    }, [socket, connected, notificationHandlers]);

    // Fetch initial notifications 
    const fetchInitialNotifications = async () => {
        try {
            const API_BASE_URL = window._env_?.API_URL ?? window._env_?.API_BASE_URL ?? process.env.REACT_APP_API_URL ?? '';
            const response = await fetch(`${API_BASE_URL || ''}/api/activities?limit=10`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.activities) {
                    setActivities(data.activities);
                    const unread = data.activities.filter(a => !a.read).length;
                    setNotificationCount(unread);
                }
            }
        } catch (error) {
            console.error('Error fetching initial notifications:', error);
        }
    };

    const shouldNotifyUser = (activity, user) => {
        // Don't show notifications for the current user's own actions if they are of type 'user'
        if (activity.userId === user?.id && activity.sourceType === 'user') {
            return false;
        }

        // Admin sees all notifications
        if (user?.role === 'admin') {
            return true;
        }

        // Regular users see their own activities or system-wide notifications
        return activity.userId === user?.id || activity.sourceType === 'system';
    };

    const showBrowserNotification = (activity) => {
        if ('Notification' in window && Notification.permission === 'granted') {
            // Format notification message based on new schema
            let title = 'New Activity';
            let body = '';

            const { type, data, sourceType } = activity;

            switch (sourceType) {
                case 'auth':
                    title = 'Authentication';
                    if (type === 'login_success') {
                        body = `${data?.username || 'User'} logged in successfully`;
                    } else if (type === 'login_failed') {
                        body = `Failed login attempt for ${data?.username || 'unknown user'}`;
                    } else {
                        body = type.replace(/_/g, ' ');
                    }
                    break;

                case 'system':
                    title = 'System Event';
                    body = type.replace(/_/g, ' ');
                    break;

                case 'whatsapp':
                    title = 'WhatsApp Event';
                    if (type === 'messages_status_updates') {
                        body = `Message ${data?.status || 'status updated'} for ${data?.recipient || 'a recipient'}`;
                    } else if (type === 'message_template_status_update') {
                        body = `Template ${data?.name || ''} is now ${data?.status || 'updated'}`;
                    } else {
                        body = type.replace(/_/g, ' ');
                    }
                    break;

                case 'user':
                    title = 'User Activity';
                    if (data?.action) {
                        body = `${data.action.replace(/_/g, ' ')} ${data?.details || ''}`;
                    } else {
                        body = type.replace(/_/g, ' ');
                    }
                    break;

                default:
                    body = type.replace(/_/g, ' ');
            }

            new Notification(title, {
                body,
                icon: '/logo192.png'
            });
        }
    };

    const markAllNotificationsAsRead = () => {
        setNotificationCount(0);
        // Update activities to mark them as read
        setActivities(prev => prev.map(activity => ({ ...activity, read: true })));
    };

    const value = {
        activities,
        notificationCount,
        markAllNotificationsAsRead,
        fetchInitialNotifications
    };

    return (
        <NotificationSocketContext.Provider value={value}>
            {children}
        </NotificationSocketContext.Provider>
    );
};

export default NotificationSocketContext;